import { ReactNode, useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { FoldVertical, UnfoldVertical, type LucideIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAgent, isAgentSurfacePath, AGENT_PANE_RESERVE_PX } from '../../contexts/AgentContext'
import { HeaderDensityContext, useHeaderDensityState } from '../../contexts/HeaderDensityContext'

export interface NavItem {
  label: string
  to: string
  icon: LucideIcon
  /** Match the route exactly (no sub-path matching) — for index routes. */
  end?: boolean
}

export interface Crumb {
  label: string
  to?: string
}

export interface TabItem {
  label: string
  to: string
  /** Match the route exactly (no sub-path matching) — for index/overview tabs. */
  end?: boolean
  /** Render a "•" group separator before this tab. */
  separatorBefore?: boolean
  /**
   * Treat the tab as active when the current path matches any of these prefixes.
   * Use for a group tab whose section spans several routes (e.g. Market →
   * Solutions / Market Search / Vendor Shortlist).
   */
  activePaths?: string[]
}

export interface AppShellProps {
  /** Primary navigation shown in the sidebar rail (Level-1, per-section). */
  nav: NavItem[]
  /** Where the sidebar logo links (per-section home). Defaults to /home. */
  homeTo?: string
  /** Sidebar footer (typically <UserMenu/>). Receives the collapsed state. */
  footer?: (collapsed: boolean) => ReactNode
  /** Extra scrollable sidebar content below the primary nav (e.g. the PM
   *  recent-programs list). Receives the collapsed state; render null when
   *  collapsed if it doesn't fit the rail. */
  sidebarExtra?: (collapsed: boolean) => ReactNode
  /** Contextual top-bar breadcrumb (Level-2). */
  breadcrumb?: Crumb[]
  /** Contextual top-bar page title. */
  title?: ReactNode
  /** Contextual top-bar right-aligned actions. */
  actions?: ReactNode
  /** Segmented tabs under the title (e.g. program sub-sections). */
  tabs?: TabItem[]
  /** Secondary tab row, shown under `tabs` (e.g. Market → Solutions / Market Search). */
  subtabs?: TabItem[]
  children: ReactNode
}

/** Whether the current path activates a tab, honoring `end` and `activePaths`. */
const isTabActive = (pathname: string, tab: TabItem): boolean => {
  const matches = (prefix: string) =>
    pathname === prefix || pathname.startsWith(`${prefix}/`)
  if (tab.activePaths?.length) return tab.activePaths.some(matches)
  if (tab.end) return pathname === tab.to
  return matches(tab.to)
}

/**
 * Contextual breadcrumb. `foldLast` hides the trailing crumb visually — in the
 * condensed header it sits inline with the <h1>, which already says the same
 * thing — while leaving it in the accessibility tree so the landmark still
 * exposes the whole path (DEV-660).
 */
const Breadcrumb = ({ crumbs, foldLast }: { crumbs: Crumb[]; foldLast?: boolean }) => (
  <nav aria-label="Breadcrumb" className="min-w-0">
    <ol className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {crumbs.map((c, i) => (
        <li
          key={i}
          className={cn(
            'flex items-center gap-1.5',
            foldLast && i === crumbs.length - 1 && 'sr-only'
          )}
        >
          {i > 0 && (
            <span className="text-muted-foreground/60" aria-hidden>
              /
            </span>
          )}
          {c.to ? (
            <Link to={c.to} className="hover:text-foreground transition-colors">{c.label}</Link>
          ) : (
            <span className="truncate">{c.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
)

/** Level-2 section tab, rendered as a pill. */
const PrimaryTab = ({ tab, active }: { tab: TabItem; active: boolean }) => (
  <span className="flex items-center">
    {tab.separatorBefore && (
      <span className="px-2 text-muted-foreground/50 select-none" aria-hidden>
        •
      </span>
    )}
    <Link
      to={tab.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors',
        active
          ? 'bg-secondary text-secondary-foreground font-medium'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
      )}
    >
      {tab.label}
    </Link>
  </span>
)

/** Level-3 sub-section tab, rendered as underlined text. */
const SecondaryTab = ({ tab, active }: { tab: TabItem; active: boolean }) => (
  <Link
    to={tab.to}
    aria-current={active ? 'page' : undefined}
    className={cn(
      'relative px-1 py-1.5 text-sm whitespace-nowrap transition-colors',
      active ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
    )}
  >
    {tab.label}
    {active && (
      <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-foreground" />
    )}
  </Link>
)

/**
 * Shared application shell — the Level-1 chrome (icon sidebar + contextual top
 * bar + segmented tabs) that every section (PM, Program, Exec, Gateway) composes
 * with its own Level-2 nav config. See DEV-405.
 */
const AppShell = ({ nav, homeTo = '/home', footer, sidebarExtra, breadcrumb, title, actions, tabs, subtabs, children }: AppShellProps) => {
  // The left nav is an icon rail by default and expands purely on hover-intent:
  // hovering the rail for ~100ms peeks it open, and it collapses shortly after
  // the pointer leaves.
  const [hovering, setHovering] = useState(false)
  const expanded = hovering
  const enterTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (enterTimer.current) clearTimeout(enterTimer.current)
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
  }, [])

  const handleRailEnter = () => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null }
    if (!hovering && !enterTimer.current) {
      enterTimer.current = setTimeout(() => { setHovering(true); enterTimer.current = null }, 100)
    }
  }
  const handleRailLeave = () => {
    if (enterTimer.current) { clearTimeout(enterTimer.current); enterTimer.current = null }
    if (hovering && !leaveTimer.current) {
      leaveTimer.current = setTimeout(() => { setHovering(false); leaveTimer.current = null }, 150)
    }
  }
  const { pathname } = useLocation()
  // When the omnipresent agent pane is open on an agent surface, reserve a
  // right-hand column so the page chrome reflows beside it (docked pane, not
  // an overlay). The pane itself is a fixed element rendered globally.
  const { isOpen: agentOpen } = useAgent()
  const reserveRight = agentOpen && isAgentSurfacePath(pathname) ? AGENT_PANE_RESERVE_PX : 0
  // Condensed on short viewports, or wherever the user pinned it. Published to
  // descendants so page-level chrome condenses in step (DEV-660).
  const density = useHeaderDensityState()
  const { condensed } = density

  const hasTabs = Boolean(tabs?.length)
  const hasSubtabs = Boolean(subtabs?.length)
  const showHeader = Boolean(breadcrumb?.length || title || actions || hasTabs || hasSubtabs)

  return (
    <HeaderDensityContext.Provider value={density}>
    <div className="h-screen flex bg-background w-full min-w-0">
      {/* Sidebar */}
      <aside
        onMouseEnter={handleRailEnter}
        onMouseLeave={handleRailLeave}
        className={cn(
          'shrink-0 flex flex-col bg-sidebar border-r border-sidebar-border transition-[width] duration-200',
          expanded ? 'w-60' : 'w-16'
        )}
      >
        <div className="h-16 flex items-center shrink-0 px-5">
          {/* Collapsed shows the roundel "O"; expanding wipes the wordmark open
              to spell out "OVERMATCH" (and reverses on collapse). It's one image
              clipped from the left, so the O never jumps — only the letters
              after it reveal. */}
          <Link
            to={homeTo}
            aria-label="Overmatch home"
            className="flex items-center overflow-hidden shrink-0 transition-[width] duration-200"
            style={{ width: expanded ? 168 : 22 }}
          >
            <img
              src="/overmatch-logo-full-navy.png"
              alt="Overmatch"
              className="block dark:hidden h-5 w-auto max-w-none shrink-0"
            />
            <img
              src="/overmatch-logo-full-white.png"
              alt=""
              aria-hidden
              className="hidden dark:block h-5 w-auto max-w-none shrink-0"
            />
          </Link>
        </div>

        <div className="flex-1 overflow-y-auto">
        <nav className="px-2 py-2 space-y-1">
          {nav.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={expanded ? undefined : item.label}
                className={({ isActive }) =>
                  cn(
                    // Fixed height + constant left padding so the icon sits in
                    // the exact same spot whether or not the label is shown —
                    // expanding only reveals the label, the icon never moves.
                    'flex items-center gap-3 rounded-lg px-3 h-9 text-sm transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground'
                  )
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                {expanded && <span className="truncate">{item.label}</span>}
              </NavLink>
            )
          })}
        </nav>
        {sidebarExtra && expanded && (
          <div className="px-2 pb-2">{sidebarExtra(!expanded)}</div>
        )}
        </div>

        {footer && <div className="border-t border-sidebar-border p-2 shrink-0">{footer(!expanded)}</div>}
      </aside>

      {/* Main column — pads right to make room for the docked agent pane. */}
      <div
        className="flex-1 flex flex-col min-w-0 transition-[padding] duration-200"
        style={{ paddingRight: reserveRight }}
      >
        {showHeader && (
          <header className="shrink-0 border-b border-border bg-background">
            {/*
              Title band. Comfortable stacks the breadcrumb over the title;
              condensed folds them onto one line, which is the single largest
              saving in the stack (DEV-660).
            */}
            <div
              data-header-row="title"
              className={cn(
                'flex gap-4 px-6',
                condensed ? 'items-center py-1.5' : 'min-h-16 items-center py-2'
              )}
            >
              <div
                className={cn(
                  'min-w-0 flex-1',
                  condensed && 'flex items-baseline gap-1.5'
                )}
              >
                {breadcrumb && breadcrumb.length > 0 && (
                  <div className={cn(!condensed && 'mb-0.5')}>
                    <Breadcrumb crumbs={breadcrumb} foldLast={condensed} />
                  </div>
                )}
                {condensed && breadcrumb && breadcrumb.length > 1 && (
                  <span className="text-xs text-muted-foreground/60 shrink-0" aria-hidden>
                    /
                  </span>
                )}
                {title && (
                  <h1
                    className={cn(
                      'font-semibold text-foreground truncate',
                      condensed ? 'text-sm' : 'text-lg'
                    )}
                  >
                    {title}
                  </h1>
                )}
              </div>
              {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
              <button
                type="button"
                onClick={density.toggle}
                aria-expanded={!condensed}
                aria-label={condensed ? 'Expand header' : 'Condense header'}
                title={condensed ? 'Expand header' : 'Condense header'}
                className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              >
                {condensed ? (
                  <UnfoldVertical className="w-4 h-4" />
                ) : (
                  <FoldVertical className="w-4 h-4" />
                )}
              </button>
            </div>

            {/*
              Tab bands. Condensed merges Level-2 and Level-3 onto one row —
              the pill/underline treatments keep the two levels legible without
              a second band costing another 40px.
            */}
            {(hasTabs || (condensed && hasSubtabs)) && (
              <div
                data-header-row="tabs"
                className={cn(
                  'flex items-center gap-1 px-6 overflow-x-auto',
                  condensed ? 'pb-1' : 'pb-2'
                )}
              >
                {tabs?.map((t) => (
                  <PrimaryTab key={t.to} tab={t} active={isTabActive(pathname, t)} />
                ))}
                {condensed && hasSubtabs && (
                  <>
                    {hasTabs && (
                      <span
                        data-testid="tab-row-divider"
                        className="mx-3 h-4 w-px bg-border shrink-0"
                        aria-hidden
                      />
                    )}
                    <div className="flex items-center gap-5">
                      {subtabs!.map((t) => (
                        <SecondaryTab key={t.to} tab={t} active={isTabActive(pathname, t)} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {!condensed && hasSubtabs && (
              <div
                data-header-row="subtabs"
                className="flex items-center gap-5 px-6 pb-2 overflow-x-auto"
              >
                {subtabs!.map((t) => (
                  <SecondaryTab key={t.to} tab={t} active={isTabActive(pathname, t)} />
                ))}
              </div>
            )}
          </header>
        )}

        <main className="flex-1 min-w-0 overflow-auto">{children}</main>
      </div>
    </div>
    </HeaderDensityContext.Provider>
  )
}

export default AppShell
